import React, { useRef } from 'react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { PIZZAS, PizzaProductExtended } from '../constants';

const getIngredientEmoji = (name: string) => {
  const map: Record<string, string> = {
    "Pepperoni": "🍕",
    "Chili Oil": "🌶️",
    "Honey": "🍯",
    "Mozzarella": "🧀",
    "Pesto": "🌿",
    "Arugula": "🍃",
    "Pine Nuts": "🥜",
    "Bufala": "🥛",
    "Truffle": "🍄",
    "Mushrooms": "🍄",
    "Pecorino": "🧀",
    "Charcoal": "🌑",
    "Burrata": "⚪",
    "Tomato": "🍅",
    "Balsamic": "🍷",
    "Basil": "🌿",
    "Nduja": "🌶️",
    "Chili Honey": "🍯",
    "Red Onion": "🧅",
    "Olives": "🫒",
    "Garlic": "🧄",
    "Onion": "🧅",
    "White Sauce": "🍶",
    "Cauliflower": "🥦"
  };
  return map[name] || "✨";
};

const PizzaSection: React.FC<{
  pizza: PizzaProductExtended,
  index: number,
  onAddToCart: (p: PizzaProductExtended) => void
}> = ({ pizza, index, onAddToCart }) => {
  const container = useRef<HTMLDivElement>(null);
  const pizzaRef = useRef<HTMLDivElement>(null);
  const infoRef = useRef<HTMLDivElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const isEven = index % 2 === 0;

  const isLightBg = ['burrata', 'bianca'].includes(pizza.id);
  const textColor = isLightBg ? 'text-[#1A3C28]' : 'text-[#F2E9D8]';
  const subtextColor = isLightBg ? 'text-[#1A3C28]/60' : 'text-[#F2E9D8]/80';
  const badgeColor = isLightBg ? 'bg-black/10' : 'bg-black/20';
  const tagBorder = isLightBg ? 'border-black/10' : 'border-white/20';

  const videoBlendMode = isLightBg ? 'mix-blend-multiply' : 'mix-blend-overlay';
  const videoOpacity = isLightBg ? 'opacity-40' : 'opacity-30';

  useGSAP(() => {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: container.current,
        start: "top 70%",
        end: "bottom 20%",
        toggleActions: "play reverse play reverse",
      }
    });

    tl.from(pizzaRef.current, {
      x: isEven ? -80 : 80,
      scale: 0.85,
      opacity: 0,
      duration: 0.9,
      ease: "power3.out"
    })
    .from(infoRef.current, {
      y: 40,
      opacity: 0,
      duration: 0.7,
      ease: "power3.out"
    }, "-=0.5")
    .from(".ingredient-tag", {
      opacity: 0,
      y: 10,
      stagger: 0.08,
      duration: 0.4,
      ease: "power2.out"
    }, "-=0.3")
    .from(".floating-tiny-ingredient", {
      opacity: 0,
      scale: 0,
      y: 20,
      stagger: 0.04,
      duration: 0.7,
      ease: "back.out(2)"
    }, "-=0.5");

    gsap.to(pizzaRef.current, {
      rotation: isEven ? 180 : -180,
      scrollTrigger: {
        trigger: container.current,
        start: "top bottom",
        end: "bottom top",
        scrub: 1,
      }
    });

    gsap.to(".floating-tiny-ingredient", {
      y: "random(-20, 20)",
      x: "random(-15, 15)",
      rotation: "random(-25, 25)",
      duration: "random(2.5, 4.5)",
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut"
    });

    gsap.to(videoContainerRef.current, {
      y: 80,
      scrollTrigger: {
        trigger: container.current,
        start: "top bottom",
        end: "bottom top",
        scrub: true
      }
    });
  }, { scope: container });

  return (
    <section
      ref={container}
      className="pizza-section min-h-screen py-16 sm:py-20 md:py-28 lg:py-32 w-full flex items-center justify-center px-4 sm:px-8 md:px-12 relative overflow-hidden"
      style={{ backgroundColor: pizza.color }}
    >
      <div ref={videoContainerRef} className="absolute inset-0 z-0 w-full h-full pointer-events-none">
        <div className={`absolute inset-0 z-10 ${isLightBg ? 'bg-black/5' : 'bg-black/30'} mix-blend-overlay`}></div>
        <video
          autoPlay
          muted
          loop
          playsInline
          className={`w-full h-full object-cover ${videoOpacity} ${videoBlendMode} scale-125`}
        >
          <source src={pizza.videoBackground} type="video/mp4" />
        </video>
      </div>

      <div className={`absolute inset-0 opacity-[0.08] flex items-center justify-center pointer-events-none select-none z-[1] ${isLightBg ? 'mix-blend-multiply' : ''}`}>
        <h2 className="font-display font-black text-[35vw] text-white uppercase leading-none drop-shadow-sm">{pizza.name[0]}</h2>
      </div>

      <div className="absolute inset-0 z-[5] pointer-events-none overflow-hidden hidden md:block">
        {[...Array(6)].map((_, i) => (
          <div
            key={`float-${i}`}
            className="floating-tiny-ingredient absolute text-3xl opacity-20 filter grayscale blur-[1px]"
            style={{
              top: `${15 + (i * 12)}%`,
              left: isEven ? `${5 + (i * 6)}%` : `${65 + (i * 6)}%`,
            }}
          >
            {getIngredientEmoji(pizza.ingredients[i % pizza.ingredients.length])}
          </div>
        ))}
      </div>

      {/* Main layout: two columns on desktop, stacked on mobile. */}
      <div className={`pizza-section-inner flex flex-col w-full max-w-7xl items-center justify-center gap-8 sm:gap-10 md:gap-12 lg:gap-16 z-10 ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
        <div className="flex-1 flex justify-center w-full">
          <div
            ref={pizzaRef}
            className="w-[70vw] h-[70vw] sm:w-[55vw] sm:h-[55vw] max-w-[360px] max-h-[360px] sm:max-w-[420px] sm:max-h-[420px] md:w-[38vw] md:h-[38vw] md:max-w-[500px] md:max-h-[500px] lg:w-[42vw] lg:h-[42vw] lg:max-w-[580px] lg:max-h-[580px] rounded-full shadow-[0_40px_80px_rgba(0,0,0,0.5)] border-[8px] sm:border-[12px] md:border-[16px] border-white/20 overflow-hidden"
          >
            <img
              src={pizza.image}
              alt={pizza.name}
              className="w-full h-full object-cover scale-110"
            />
          </div>
        </div>

        <div
          ref={infoRef}
          className={`flex-1 ${textColor} flex flex-col justify-center gap-4 sm:gap-5 md:gap-6 text-center ${isEven ? 'md:text-left' : 'md:text-right'}`}
        >
          <div className="space-y-2 sm:space-y-3">
            <span className={`inline-block px-3.5 py-1.5 ${badgeColor} backdrop-blur-md rounded-full text-xs md:text-sm font-black uppercase tracking-[0.3em]`}>
              {pizza.tagline}
            </span>
            <h2 className="font-display font-black uppercase leading-[0.88] tracking-tighter text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl break-words max-w-full">
              {pizza.name}
            </h2>
          </div>

          <p className={`font-bold ${subtextColor} leading-relaxed text-sm sm:text-base md:text-lg lg:text-xl max-w-xl mx-auto md:mx-0`}>
            {pizza.description}
          </p>

          <div className={`flex flex-wrap gap-2 justify-center ${isEven ? 'md:justify-start' : 'md:justify-end'}`}>
            {pizza.ingredients.map((ing, i) => (
              <span key={i} className={`ingredient-tag flex items-center gap-1.5 font-black uppercase tracking-widest bg-white/10 px-3 md:px-4 py-1.5 md:py-2 rounded-full border ${tagBorder} text-xs md:text-sm`}>
                <span>{getIngredientEmoji(ing)}</span>
                {ing}
              </span>
            ))}
          </div>

          <div className={`flex flex-col sm:flex-row items-center gap-4 sm:gap-6 md:gap-8 pt-2 sm:pt-4 ${isEven ? 'md:justify-start' : 'md:justify-end'}`}>
            <span className="font-black font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl">{pizza.price}</span>
            <button
              onClick={() => onAddToCart(pizza)}
              className="bg-[#1A3C28] text-white px-8 md:px-10 py-3.5 md:py-4 rounded-full font-black uppercase text-xs md:text-sm tracking-widest hover:scale-105 active:scale-95 transition-all shadow-2xl"
            >
              Add to Order
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

const GustoRotator: React.FC<{ onAddToCart: (p: PizzaProductExtended) => void }> = ({ onAddToCart }) => {
  return (
    <div className="w-full">
      {PIZZAS.map((pizza, i) => (
        <PizzaSection key={pizza.id} pizza={pizza} index={i} onAddToCart={onAddToCart} />
      ))}
    </div>
  );
};

export default GustoRotator;
